import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingHomePage from '../../pages/marketing/MarketingHomePage';
import { isAppLaunch } from '../../utils/launchContext';
import { getDefaultRoute } from '../../utils/defaultRoute';
import { tryResumeVenueDraft } from '../../utils/venueDraftResume';
import { peekLaunchDeepLink, consumeLaunchDeepLink } from '../../utils/nativeDeepLinks';
import { consumePendingPushTapRoute } from '../../utils/fcmPush';
import { getPendingPartnerIntent, clearPendingPartnerIntent, shouldForceRolePicker } from '../../utils/partnerIntent';

/**
 * Decides what '/' renders:
 *   - If the user is logged in AND has an unsubmitted venue draft → resume
 *     it at Step 1 with a "Welcome back" toast.
 *   - Otherwise, if the visit looks like an installed-app launch (TWA/PWA/
 *     standalone) OR the user is already authenticated, send them into
 *     the app via the existing role-aware default route.
 *   - Otherwise, render the public marketing home page.
 */
export function SmartRootRouter({ user }) {
  const navigate = useNavigate();

  useEffect(() => {
    // STALE-EFFECT GUARD: this effect belongs to the '/' route, but by the
    // time it runs the native deep-link navigator may have ALREADY pushed
    // /invite/XXXX (cold-start App Link). Without this guard the redirect
    // below would replace the invite with /login or the role home — the
    // exact cold-start deep-link bug reported on Android.
    if (window.location.pathname !== '/') return;
    // A buffered native cold-start deep link (App Link launch intent or
    // Play Store install referrer) ALWAYS wins over any home redirect —
    // it's how the user got here. Works for logged-in AND logged-out users:
    // the invite entry pages render their own login/register CTAs.
    const launchDeepLink = peekLaunchDeepLink();
    if (launchDeepLink) {
      consumeLaunchDeepLink();
      navigate(launchDeepLink, { replace: true });
      return;
    }
    // A buffered FCM push-tap route (e.g. "Tip your host" → /guest/tips/XYZ)
    // wins over the generic role-home redirect so the user lands on the
    // exact screen the notification was pointing at. Only honoured when
    // the user is actually authenticated — otherwise ProtectedRoute would
    // bounce it to /login and we'd lose the deep link.
    if (user) {
      const pendingPushRoute = consumePendingPushTapRoute();
      if (pendingPushRoute) {
        navigate(pendingPushRoute, { replace: true });
        return;
      }
    }
    if (user) {
      // Auto-resume the venue onboarding draft for anyone who actually has
      // one — the underlying resume helper is a no-op if no draft exists,
      // so this is safe for hosts, staff, admins and plain guests too.
      // Extra guards keep us out of the wizard for accounts that have
      // clearly moved past onboarding (approved venue owners, admins,
      // staff shells).
      const tryResume = async () => {
        const partnerCandidate = (
          user.role === 'venue_owner'
          || user.role === 'guest'
        ) && (user.venue_status !== 'approved');
        if (partnerCandidate) {
          const token = localStorage.getItem('token');
          const resumed = await tryResumeVenueDraft({ token, navigate });
          if (resumed) return;
        }
        // A fresh partner signup who tapped "Airbnb Host" or "Food or
        // Drink Venue" on /select-role gets routed straight to the
        // matching onboarding step — even after a Capacitor cold-launch
        // that lost the in-flight route. Clears itself once the partner
        // application is submitted so the user's real default route
        // wins from then on.
        const partnerIntent = getPendingPartnerIntent();
        if (partnerIntent === 'host' && !user.host_status) {
          navigate('/host-verify-listing', { replace: true });
          return;
        }
        if (partnerIntent === 'venue' && !user.venue_status) {
          navigate('/venue/onboarding/1', { replace: true });
          return;
        }
        if (partnerIntent) {
          // Partner intent no longer relevant (application already
          // submitted or user opted back into guest). Wipe it so it
          // stops re-routing on later cold-launches.
          clearPendingPartnerIntent();
        }
        // Fresh guest who hasn't yet made a role choice (no partner
        // intent, no host/venue status, backend role still 'guest') is
        // routed to the role picker so a Capacitor cold-launch on '/'
        // after a fresh /register or Google signup doesn't silently
        // dump them into the guest home.
        if (shouldForceRolePicker(user)) {
          navigate('/select-role', { replace: true });
          return;
        }
        navigate(getDefaultRoute(user), { replace: true });
      };
      tryResume();
      return;
    }
    // Anonymous app-launch (e.g. TWA from Play Store) → existing intro/auth flow
    if (isAppLaunch()) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Anonymous browser visitor → marketing site
  if (user) return null;
  if (isAppLaunch()) return null;
  return <MarketingHomePage />;
}
